
export default function TechnicalSpecifications() {
  const specifications = [
    {
      category: 'Structural Framework',
      icon: 'ri-building-4-line',
      specs: [
        {
          name: 'Main Frame',
          value: 'Galvanized Steel',
          description: 'High-strength steel frame with anti-corrosion galvanized coating for maximum durability'
        },
        {
          name: 'Frame Thickness',
          value: '3.0mm - 4.0mm',
          description: 'Heavy-duty steel construction ensuring structural integrity and longevity'
        },
        {
          name: 'Load Capacity',
          value: '150 kg/m²',
          description: 'Engineered to withstand significant loads including snow, wind, and occupancy'
        }
      ]
    },
    {
      category: 'Exterior Shell',
      icon: 'ri-shield-line',
      specs: [
        {
          name: 'Outer Panel',
          value: 'Aluminium Alloy',
          description: 'Premium aluminium alloy panels with weather-resistant finish and modern aesthetic'
        },
        {
          name: 'Panel Thickness',
          value: '0.8mm - 1.2mm',
          description: 'Optimal thickness balancing weight, strength, and thermal performance'
        },
        {
          name: 'Surface Treatment',
          value: 'Powder Coating',
          description: 'Durable powder-coated finish resistant to UV, scratches, and environmental wear'
        }
      ]
    },
    {
      category: 'Insulation System',
      icon: 'ri-temp-cold-line',
      specs: [
        {
          name: 'Core Material',
          value: 'Polyurethane Foam',
          description: 'High-density polyurethane insulation providing superior thermal efficiency'
        },
        {
          name: 'Insulation Thickness',
          value: '50mm - 75mm',
          description: 'Optimal insulation depth for year-round climate control and energy efficiency'
        },
        {
          name: 'Thermal Rating',
          value: 'R-Value 4.5-6.0',
          description: 'Excellent thermal resistance reducing heating and cooling costs significantly'
        }
      ]
    },
    {
      category: 'Windows & Doors',
      icon: 'ri-door-open-line',
      specs: [
        {
          name: 'Window Type',
          value: 'Double-Glazed',
          description: 'Energy-efficient double-pane windows with aluminium frames and thermal breaks'
        },
        {
          name: 'Door Material',
          value: 'Aluminium Alloy',
          description: 'Reinforced aluminium doors with multi-point locking systems for security'
        },
        {
          name: 'Glass Specification',
          value: '5mm + 9A + 5mm',
          description: 'Low-E coated glass with argon gas fill for optimal insulation and UV protection'
        }
      ]
    }
  ];

  return (
    <section id="technical-specs" className="py-20 sm:py-28 lg:py-36 bg-gradient-to-b from-[#000000] via-[#0D1117] to-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 sm:mb-20">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-thin mb-6 text-white">
            Technical <span className="text-cyan-400">Specifications</span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Built with premium materials and advanced engineering for uncompromising quality and performance
          </p>
        </div>

        {/* Specifications Grid */}
        <div className="space-y-12">
          {specifications.map((section, index) => (
            <div key={index} className="bg-black/50 backdrop-blur-sm border border-cyan-500/20 rounded-2xl p-8 sm:p-10 hover:border-cyan-500/40 hover:shadow-[0_0_30px_rgba(0,217,255,0.15)] transition-all duration-300">
              {/* Category Header */}
              <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 bg-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-[0_0_20px_rgba(0,217,255,0.4)]">
                  <i className={`${section.icon} text-2xl text-black`}></i>
                </div>
                <h3 className="text-2xl sm:text-3xl font-light text-white">{section.category}</h3>
              </div>

              {/* Specs Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {section.specs.map((spec, idx) => (
                  <div key={idx} className="bg-black/30 border border-cyan-500/10 rounded-xl p-6 hover:border-cyan-500/30 transition-all">
                    <div className="flex items-start justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide">{spec.name}</h4>
                      <i className="ri-checkbox-circle-line text-lg text-cyan-500"></i>
                    </div>
                    <p className="text-xl font-medium text-cyan-400 mb-3">{spec.value}</p>
                    <p className="text-sm text-gray-400 leading-relaxed">{spec.description}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Additional Features */}
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: 'ri-water-flash-line', title: 'Waterproof', desc: 'IP65 rated protection' },
            { icon: 'ri-fire-line', title: 'Fire Resistant', desc: 'Class A fire rating' },
            { icon: 'ri-earthquake-line', title: 'Seismic Resistant', desc: 'Grade 8 earthquake proof' },
            { icon: 'ri-windy-line', title: 'Wind Resistant', desc: 'Up to 120 km/h winds' }
          ].map((feature, index) => (
            <div key={index} className="bg-cyan-500 text-black rounded-xl p-6 text-center hover:scale-105 hover:shadow-[0_0_30px_rgba(0,217,255,0.5)] transition-all">
              <div className="w-12 h-12 bg-black/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className={`${feature.icon} text-2xl`}></i>
              </div>
              <h4 className="text-lg font-medium mb-2">{feature.title}</h4>
              <p className="text-sm text-black/70">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
